import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { STAGES } from "./leadConfig";
import { LeadRow } from "./LeadRow";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("vlx_admin_token") || ""}` });

export const LeadsTab = ({ onConvert }) => {
  const [source, setSource] = useState("quote");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stageFilter, setStageFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/leads/${source}`, { headers: auth() });
      setLeads(data);
    } catch (e) { if (e.response?.status !== 401) toast.error("Failed to load leads"); }
    setLoading(false);
  }, [source]);
  useEffect(() => { load(); }, [load]);

  const update = async (lead, patch) => {
    try {
      const { data } = await axios.patch(`${API}/leads/${source}/${lead.id}`, patch, { headers: auth() });
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? data : l)));
    } catch { toast.error("Update failed"); }
  };

  const convert = async (q) => {
    try {
      await axios.post(`${API}/quotes/${q.id}/convert`, {}, { headers: auth() });
      toast.success(`Converted ${q.name} → Estimate`);
      onConvert?.();
    } catch { toast.error("Failed to convert"); }
  };

  const counts = useMemo(() => leads.reduce((acc, l) => ({ ...acc, [l.stage || "new"]: (acc[l.stage || "new"] || 0) + 1 }), {}), [leads]);
  const visible = stageFilter ? leads.filter((l) => (l.stage || "new") === stageFilter) : leads;

  return (
    <div data-testid="leads-tab" className="space-y-5">
      <div className="flex gap-6 border-b border-[#c99658]/25">
        {[["quote", "Quote Requests"], ["catalog", "Catalog Downloads"]].map(([id, label]) => (
          <button key={id} data-testid={`leads-source-${id}`} onClick={() => { setSource(id); setStageFilter(""); }}
            className={`pb-2 text-[11px] tracking-[0.25em] uppercase font-raleway border-b-2 ${source === id ? "border-[#c99658] text-[#0f2420]" : "border-transparent text-neutral-500 hover:text-[#7a5b2f]"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-2">
        <button data-testid="stage-filter-all" onClick={() => setStageFilter("")}
          className={`p-3 text-left border transition-colors ${!stageFilter ? "border-[#0f2420] bg-[#0f2420] text-white" : "border-[#c99658]/25 bg-white hover:border-[#7a5b2f]"}`}>
          <div className="text-2xl font-cormorant leading-none">{leads.length}</div>
          <div className="text-[9.5px] tracking-[0.2em] uppercase font-raleway mt-1 opacity-80">All</div>
        </button>
        {STAGES.map((s) => (
          <button key={s.id} data-testid={`stage-filter-${s.id}`} onClick={() => setStageFilter(stageFilter === s.id ? "" : s.id)}
            className={`p-3 text-left border transition-colors ${stageFilter === s.id ? "border-[#0f2420] ring-1 ring-[#0f2420]" : "border-[#c99658]/25 hover:border-[#7a5b2f]"} ${s.color}`}>
            <div data-testid={`stage-count-${s.id}`} className="text-2xl font-cormorant leading-none">{counts[s.id] || 0}</div>
            <div className="text-[9.5px] tracking-[0.2em] uppercase font-raleway mt-1 leading-tight">{s.label}</div>
          </button>
        ))}
      </div>

      {loading && <p className="text-neutral-600">Loading…</p>}
      {!loading && !visible.length && <p data-testid="leads-empty" className="text-neutral-600">No leads here yet.</p>}
      <div className="space-y-3">
        {visible.map((l) => (
          <LeadRow key={l.id} lead={l} source={source} onUpdate={(p) => update(l, p)} onConvert={source === "quote" ? () => convert(l) : undefined} />
        ))}
      </div>
    </div>
  );
};
